import React, { useEffect, useState } from 'react';
import { useMutation, gql } from '@apollo/client';

import Chat from './Chat';
import { MESSAGE_ATTRIBUTES } from './ChatWindowContainer';

export const CREATE_CHAT_MUTATION = gql`
  mutation createChat($customer: ID!, $url: String!) {
    createChat(customer: $customer, url: $url) {
      id
      messages {
        ...MessageAttributes
      }
    }
  }
  ${MESSAGE_ATTRIBUTES}
`;

interface ChatContainerProps {
  customerId?: string;
  openByDefault?: boolean;
  collapsible?: boolean;
}

function ChatContainer({
  customerId,
  openByDefault,
  collapsible,
}: ChatContainerProps): JSX.Element {
  const [externalCustomerId, setExternalCustomerId] = useState(customerId);
  const [parentUrl, setParentUrl] = useState(window.location.href);
  const [createChat, { data, loading, error }] = useMutation(CREATE_CHAT_MUTATION);

  useEffect(() => {
    function handleMessage(e: {
      data: { method: string; chatCustomerId: string; parentUrl: string };
    }) {
      if (e.data.method === 'setChatCustomerId') {
        setExternalCustomerId(e.data.chatCustomerId);
      } else if (e.data.method === 'setParentUrl') {
        setParentUrl(e.data.parentUrl);
      }
    }
    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ method: 'chatAppLoaded' }, '*');
    return function cleanup() {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!externalCustomerId || !parentUrl) {
      return;
    }

    createChat({
      variables: {
        customer: externalCustomerId,
        url: parentUrl,
      },
    })
      .then(() => {
        window.parent.postMessage({ method: 'chatCreated' }, '*');
      })
      .catch((createChatError) => {
        console.warn(createChatError);
      });
  }, [createChat, externalCustomerId, parentUrl]);

  if (!externalCustomerId || !parentUrl) {
    return <div data-testid="chat-app-loading" />;
  }

  if (error) {
    return <div data-testid="chat-container-error" />;
  }

  if (loading || !data) {
    return <div data-testid="chat-container-loading" />;
  }

  return <Chat uuid={data.createChat.id} openByDefault={openByDefault} collapsible={collapsible} />;
}

export default ChatContainer;
